// src/contexts/VisitContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Visit, Filter } from "@visitproof/types";
import { VisitService } from "../services/VisitService";
import { useAccount } from "./AccountContext";
import { useAuth } from "./AuthContext";
import { View } from "react-big-calendar";

interface SchedulerContextProps {
  filter: Filter | null;
  setFilter: (filter: Filter | null) => void;
  visits: Visit[];
  createVisit: (
    visitData: Omit<
      Visit,
      "id" | "createdAt" | "updatedAt" | "accountId" | "createdBy"
    >,
  ) => Promise<string>;
  calendarView: View;
  setCalendarView: (view: View) => void;
  loading: boolean;
  error: string | null;
}

const SchedulerContext = createContext<SchedulerContextProps | undefined>(
  undefined,
);

export const useScheduler = (): SchedulerContextProps => {
  const context = useContext(SchedulerContext);
  if (!context) {
    throw new Error("useScheduler must be used within a SchedulerProvider");
  }
  return context;
};

export const SchedulerProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { account } = useAccount();
  const [calendarView, setCalendarView] = useState<View>(
    (localStorage.getItem("calendarView") as View) || "month",
  );
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter | null>(null);

  const reloadVisits = async () => {
    if (!account?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let visitList = await VisitService.getAllVisits(account.id);
      if (filter?.caseId) {
        visitList = visitList.filter((visit) => visit.caseId === filter.caseId);
      }
      if (filter?.supervisorId) {
        visitList = visitList.filter(
          (visit) => visit.supervisorId === filter.supervisorId,
        );
      }
      setVisits(visitList);
    } catch (err) {
      setError("Failed to load visits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    localStorage.setItem("calendarView", calendarView);
  }, [calendarView]);

  useEffect(() => {
    reloadVisits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, account?.id]);

  const createVisit = async (
    caseData: Omit<
      Visit,
      "id" | "createdAt" | "updatedAt" | "accountId" | "createdBy"
    >,
  ): Promise<string> => {
    if (!account?.id || !user?.uid) {
      throw new Error("Account or user not available");
    }
    const newCaseId = await VisitService.createVisit({
      ...caseData,
      accountId: account.id,
      createdBy: user.uid,
    });
    await reloadVisits();
    return newCaseId;
  };

  return (
    <SchedulerContext.Provider
      value={{
        filter,
        setFilter,
        visits,
        loading,
        error,
        createVisit,
        calendarView,
        setCalendarView,
      }}
    >
      {children}
    </SchedulerContext.Provider>
  );
};
